/**
 * RequireAuth.tsx — Route guard enforcing the Cognito login wall (AUTH-01)
 *
 * Rendering behaviour:
 *
 *  loading=true          → spinner (prevents flash of login form on refresh)
 *  user=null, loading=false → LoginForm (unauthenticated visitor cannot reach the map)
 *  user set              → renders children (the map and all protected content)
 *
 * Wrap any top-level route in <RequireAuth> to enforce Cognito auth day-1.
 * The AuthContext (AuthProvider) must be an ancestor in the tree.
 *
 * AUTH-01: visitor → login wall → sign in → children rendered.
 * AUTH-02: on page refresh, AuthProvider.useEffect re-runs getSession(), which
 *          auto-refreshes via the refresh token; loading=true until it resolves,
 *          so RequireAuth shows the spinner instead of a flash of the login form.
 */
import type { ReactNode } from 'react'
import { useAuth } from './useAuth'
import { LoginForm } from './LoginForm'
import { hasSharedLocation } from '../lib/shareLink'

interface RequireAuthProps {
  children: ReactNode
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { user, loading } = useAuth()

  // While the initial session check is in-flight (page load / refresh)
  if (loading) {
    return (
      <div
        data-testid="auth-loading"
        className="flex items-center justify-center h-dvh text-sm text-muted-foreground font-sans"
      >
        Loading…
      </div>
    )
  }

  // No authenticated session — show the login wall (AUTH-01). T-500: if the app was opened via a
  // share deep-link (the hash carries a shared location), tell the recipient why they hit the wall.
  // The hash is preserved across this swap, so after they sign in MapShell boot-hydrates the
  // shared location (see useMapUrlState.initial) — the "post-auth redirect" needs no router.
  if (!user) {
    return <LoginForm sharedLink={hasSharedLocation(window.location.hash)} />
  }

  // Authenticated — render protected content
  return <>{children}</>
}
